import React, {
  forwardRef,
  ReactElement,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react'
import { useIntl } from 'react-intl'
import { AccessibilityRole, Platform, TextStyle, View, ViewStyle } from 'react-native'

import BottomSheetGorhom from '@gorhom/bottom-sheet'
import { noop } from 'lodash-es'
import { CSSObject } from 'styled-components'
import styled from 'styled-components/native'
import { v4 as uuidv4 } from 'uuid'

import { AccessibilityRolesNative, IS_WEB } from '../../constants'
import { useOnClickOutside } from '../../hooks/useOnClickOutside'
import { BottomSheet } from '../../molecules/bottomSheet/BottomSheet'
import { BodyTextSize } from '../../styles/typeStyles'
import { ThemeType } from '../../utils/themes/ThemeProvider'
import { tID } from '../../utils/utils'
import { BaseButton, ButtonSize, ButtonType } from '../baseButton/BaseButton'
import { BodyText } from '../bodyText/BodyText'
import {
  DropdownMenu,
  DropdownMenuProps,
  GetMenuItemStyle,
  MENU_SELECTED_ITEM_STYLE,
} from '../formElements/typeAhead/DropdownMenu'
import { TypeAheadCustomOptionsConfig, ValueFetchMode } from '../formElements/typeAhead/TypeAheadCustomOptionsConfig'
import { ChevronIcon, ChevronIconDirection } from '../icons/ChevronIcon'
import { TruncatedText } from '../truncate/TruncatedText'

export type DropdownButtonHandle = {
  forceClose: () => void
}
export interface DropdownMenuItem {
  text: string
  testId?: string
  icon?: React.ReactNode
  id?: string
  dropdownOptionText?: string
  selectHandler: (event?: React.SyntheticEvent<unknown>) => void
  /** Optional subtext displayed below the main text */
  subText?: string
  /** If true, renders as a non-clickable header item */
  header?: boolean
}

export type DropdownButtonProps<T> = {
  accessibilityLabel?: string
  buttonTextWidth?: string
  buttonType?: ButtonType
  buttonSize?: ButtonSize
  customButtonText?: string
  customTextSize?: BodyTextSize
  customTextColor?: string
  customTextStyle?: TextStyle
  dropdownWidth?: string | number
  dropdownItems: DropdownMenuItem[]
  dropdownAccessibilityLabel?: string
  dropdownTestID?: string
  hideChevron?: boolean
  testID?: string
  triggerTestID?: string
  style?: ViewStyle
  dropdownStyle?: ViewStyle
  hideDefaultValue?: boolean
  preselectedValue?: string
  fullWidth?: boolean
  leftIcon?: ReactElement | null
  leftIconNoMargin?: boolean
  iconAtEnd?: boolean
  iconColor?: string
  buttonPlaceholderText?: string
  label?: React.ReactNode
  buttonFooter?: React.ReactNode
  focusBorderStyle?: CSSObject
  containerStyle?: ViewStyle
  customOptionsConfig?: TypeAheadCustomOptionsConfig<T>
  disableSelectedValueHighlight?: boolean
  disableCenteredContent?: boolean
  showBottomSheet?: boolean
  bottomSheetProps?: {
    menuItemHeight: number
    topPadding: number
    bottomPadding: number
    headerComponent?: React.ReactNode
    headerComponentHeight: number
    footerComponent?: React.ReactNode
    footerComponentHeight: number
    snapPoints?: (string | number)[]
    enableContentPanningGesture?: boolean
    useWebPortal?: boolean
    renderChildrenWithoutContainer?: boolean
    showCloseIcon?: boolean
  }
  onPress?: (isDropdownOpen?: boolean) => void
  dropdownMenuProps?: {
    showsVerticalScrollIndicator?: boolean
    scrollEnabled?: boolean
    scrollEnabledWithinBottomSheet?: boolean
  }
  value?: string
  shouldCloseMenuOnOptionPress?: boolean
  controlledValues?: string[]
  selectedItemStyle?: MENU_SELECTED_ITEM_STYLE | null
  shouldTruncateButtonText?: boolean
  getMenuItemStyle?: GetMenuItemStyle
  listFooterComponent?: React.ReactElement
  listFooterComponentStyle?: ViewStyle
  customDropdownMenuComponent?: React.ComponentType<DropdownComponentProps>
  customDropdownMenuComponentProps?: Record<string, unknown>
  contentContainerStyle?: CSSObject
  /** Shows a loading indicator on the button */
  isLoading?: boolean
  /** Disables the button */
  isDisabled?: boolean
  /** Custom button content - when provided, overrides the default text display */
  children?: React.ReactNode
  /** Maximum width for the dropdown menu */
  menuMaxWidth?: string | number
  /** Selectors for portaled content that should be treated as part of the open dropdown. */
  outsideClickIgnoreSelectors?: string[]
  /** Use simple header style (smaller text, secondary color) instead of large bold headers */
  simpleHeaderStyle?: boolean
  /** Use active/teal color for menu item text */
  useActiveTextColor?: boolean
}

const ButtonTextContainer = styled.View<{ width?: string | number }>(({ width }) => ({
  ...(width && { width: width }),
}))

const DropdownContainer = styled.View<{ theme: ThemeType; width?: string | number; maxWidth?: string | number }>(
  ({ theme, width, maxWidth }) => ({
    position: 'absolute',
    backgroundColor: theme.colors.backgroundModal,
    padding: `${theme.spacing['16px']} 0`,
    borderRadius: theme.spacing['8px'],
    boxShadow: `0 2px 16px ${theme.colors.shadowLow}`,
    ...(width && { width: width }),
    ...(maxWidth && { maxWidth: maxWidth }),
    top: '100%',
  }),
)

const BottomSheetMenuContainer = styled.View<{
  theme: ThemeType
  height: number
  paddingTop: number
  paddingBottom: number
}>(({ theme, height, paddingTop, paddingBottom }) => ({
  padding: `${paddingTop}px ${theme.spacing['16px']} ${paddingBottom}px ${theme.spacing['16px']}`,
  height: `${height}px`,
}))

const MenuItemContainer = styled.View({
  flexDirection: 'column',
  alignItems: 'flex-start',
})

const SubTextContainer = styled(BodyText)<{ theme: ThemeType }>(({ theme }) => ({
  color: theme.colors.textSecondary,
}))
export interface DropdownComponentProps extends Omit<DropdownMenuProps<DropdownMenuItem>, 'options' | 'value'> {
  dropdownOptionProps: { options: string[]; customOptions?: DropdownMenuItem[] }
  menuValues: string[]
  dropdownMenuProps?: {
    showsVerticalScrollIndicator?: boolean
    scrollEnabled?: boolean
    scrollEnabledWithinBottomSheet?: boolean
  }
  simpleHeaderStyle?: boolean
  useActiveTextColor?: boolean
}

const DropdownMenuComponent: React.FC<DropdownComponentProps> = ({
  dropdownOptionProps,
  onOptionPress,
  menuValues,
  customOptionsConfig,
  dropdownMenuProps,
  selectedItemStyle,
  getMenuItemStyle,
  listFooterComponent,
  listFooterComponentStyle,
  scrollEnabledWithinBottomSheet = false,
  simpleHeaderStyle = false,
  useActiveTextColor = false,
}) => (
  <DropdownMenu
    {...dropdownOptionProps}
    onOptionPress={onOptionPress}
    value={menuValues}
    customOptionsConfig={customOptionsConfig}
    showsVerticalScrollIndicator={dropdownMenuProps?.showsVerticalScrollIndicator}
    scrollEnabled={dropdownMenuProps?.scrollEnabled}
    selectedItemStyle={selectedItemStyle}
    getMenuItemStyle={getMenuItemStyle}
    listFooterComponent={listFooterComponent}
    listFooterComponentStyle={listFooterComponentStyle}
    scrollEnabledWithinBottomSheet={scrollEnabledWithinBottomSheet}
    simpleHeaderStyle={simpleHeaderStyle}
    useActiveTextColor={useActiveTextColor}
  />
)

export const DropdownButton = forwardRef<DropdownButtonHandle, DropdownButtonProps<any>>(
  (
    {
      accessibilityLabel,
      buttonTextWidth,
      buttonType = ButtonType.SECONDARY,
      buttonSize,
      customButtonText,
      dropdownWidth,
      dropdownItems,
      dropdownAccessibilityLabel,
      dropdownTestID,
      hideChevron = false,
      customTextSize,
      customTextColor,
      customTextStyle,
      style,
      dropdownStyle,
      testID = 'DropdownButton',
      triggerTestID,
      preselectedValue,
      fullWidth = false,
      leftIcon,
      leftIconNoMargin,
      iconAtEnd = false,
      iconColor,
      buttonPlaceholderText,
      label,
      buttonFooter,
      focusBorderStyle,
      containerStyle,
      customOptionsConfig,
      disableSelectedValueHighlight = false,
      disableCenteredContent = false,
      showBottomSheet = false,
      bottomSheetProps,
      onPress = noop,
      dropdownMenuProps,
      value,
      shouldCloseMenuOnOptionPress = true,
      controlledValues,
      selectedItemStyle,
      shouldTruncateButtonText = false,
      getMenuItemStyle,
      listFooterComponent,
      listFooterComponentStyle,
      hideDefaultValue = false,
      customDropdownMenuComponent,
      customDropdownMenuComponentProps,
      contentContainerStyle,
      isLoading = false,
      isDisabled = false,
      children,
      menuMaxWidth,
      outsideClickIgnoreSelectors,
      simpleHeaderStyle = false,
      useActiveTextColor = false,
    }: DropdownButtonProps<DropdownMenuItem>,
    ref,
  ) => {
    const dropdownContainerRef = useRef(null)
    const buttonWidth = useRef(0)
    const { formatMessage } = useIntl()

    const defaultSelectedDropdownItem = useMemo(() => {
      if (hideDefaultValue) {
        return null
      }
      return (
        preselectedValue
          ? dropdownItems.find(
              (option) => option.text === preselectedValue || option.dropdownOptionText === preselectedValue,
            )
          : dropdownItems[0]
      ) as DropdownMenuItem
    }, [dropdownItems, hideDefaultValue, preselectedValue])

    const [selectedValue, setSelectedValue] = useState<DropdownMenuItem | null | undefined>(defaultSelectedDropdownItem)
    const [isDropdownOpen, setIsDropdownOpen] = useState(false)

    const [isDropdownTouched, setIsDropdownTouched] = useState(false)

    const dropdownContainerRefID = 'DropDownContainer'
    const triggerButtonRefID = useMemo(() => 'TriggerButton-' + uuidv4().substring(0, 8), [])

    // Android truncation needs it's parent component to have a fixed width to work correctly
    const buttonTextContainerWidth =
      Platform.OS === 'android' && shouldTruncateButtonText ? buttonWidth.current - 16 * 2 : buttonTextWidth

    useImperativeHandle(ref, () => ({
      forceClose: () => {
        if (showBottomSheet) {
          bottomSheetRef?.current?.close()
        }
        setIsDropdownOpen(false)
      },
    }))

    useOnClickOutside(
      dropdownContainerRefID,
      dropdownContainerRef,
      () => {
        setIsDropdownOpen(false)
      },
      triggerButtonRefID,
      outsideClickIgnoreSelectors,
    )

    const dropdownOptions = dropdownItems.map((option) => option.dropdownOptionText ?? option.text)

    // Check if any items have header or subText properties that need special rendering
    const hasHeaderOrSubTextItems = useMemo(
      () => dropdownItems.some((item) => item.header || item.subText),
      [dropdownItems],
    )

    // Map dropdown items to include menuItem_header for DropdownMenu compatibility
    const mappedDropdownItems = useMemo(() => {
      if (!hasHeaderOrSubTextItems) return dropdownItems
      return dropdownItems.map((item) => ({
        ...item,
        menuItem_header: item.header,
      }))
    }, [dropdownItems, hasHeaderOrSubTextItems])

    // Create a default customOptionsConfig for items with header or subText
    const defaultCustomOptionsConfig = useMemo(() => {
      if (customOptionsConfig || !hasHeaderOrSubTextItems) return undefined
      return new TypeAheadCustomOptionsConfig<DropdownMenuItem>(
        mappedDropdownItems,
        (option, displayText) => {
          if (option.subText) {
            return (
              <MenuItemContainer>
                {displayText}
                <SubTextContainer text={option.subText} size={BodyTextSize.SMALL} />
              </MenuItemContainer>
            )
          }
          return displayText
        },
        undefined,
        'text',
        'text',
        ValueFetchMode.LEGACY_LABEL,
      )
    }, [customOptionsConfig, hasHeaderOrSubTextItems, mappedDropdownItems])

    const effectiveCustomOptionsConfig = customOptionsConfig ?? defaultCustomOptionsConfig

    let dropdownOptionProps: { options: string[]; customOptions?: DropdownMenuItem[] }
    if (effectiveCustomOptionsConfig) {
      dropdownOptionProps = { options: dropdownOptions, customOptions: mappedDropdownItems }
    } else {
      dropdownOptionProps = { options: dropdownOptions }
    }

    const bottomSheetRef = useRef<BottomSheetGorhom>(null)

    const dropdownItemsBottomSheetMenuHeight = bottomSheetProps
      ? dropdownItems.length * bottomSheetProps.menuItemHeight +
        bottomSheetProps.topPadding +
        bottomSheetProps.bottomPadding +
        bottomSheetProps.headerComponentHeight +
        bottomSheetProps.footerComponentHeight
      : 0

    const closeBottomSheet = useCallback(() => {
      bottomSheetRef?.current?.close()
      setIsDropdownOpen(false)
    }, [])

    useEffect(() => {
      if (value) {
        // value is the id of a dropdown menu item, so lets find that value and then setSelectedValue to that item
        const selectedDropdownItem = dropdownItems.find((option) => option.id === value)
        if (
          selectedDropdownItem &&
          (selectedDropdownItem.id !== selectedValue?.id || selectedDropdownItem.text !== selectedValue?.text)
        ) {
          setSelectedValue(selectedDropdownItem)
        }
      }
    }, [dropdownItems, selectedValue, value])

    const menuValues = useMemo(() => {
      if (controlledValues) {
        return controlledValues
      } else if (selectedValue && !disableSelectedValueHighlight) {
        if (effectiveCustomOptionsConfig) {
          return [effectiveCustomOptionsConfig.getValueForItem(selectedValue)]
        }
        return [selectedValue?.dropdownOptionText ?? selectedValue.text]
      } else {
        return ['']
      }
    }, [controlledValues, disableSelectedValueHighlight, selectedValue, effectiveCustomOptionsConfig])

    const defaultButtonText: JSX.Element = (
      <BodyText
        text={
          !isDropdownTouched && buttonPlaceholderText
            ? buttonPlaceholderText
            : (customButtonText ?? selectedValue?.text)
        }
        color={customTextColor}
        size={customTextSize}
        style={customTextStyle}
      />
    )

    const buttonText = children ?? defaultButtonText
    const dropdownMenuComponentSharedProps = {
      dropdownOptionProps: dropdownOptionProps,
      onOptionPress: (selected: any, customOption: any) => {
        setIsDropdownTouched(true)
        const selectedDropdownItem =
          effectiveCustomOptionsConfig?.getValueFetchMode() === ValueFetchMode.ID
            ? dropdownItems.find((option) => option.id === customOption.id)
            : dropdownItems.find((option) => option.text === selected || option.dropdownOptionText === selected)
        !customButtonText && setSelectedValue(selectedDropdownItem ?? defaultSelectedDropdownItem)
        selectedDropdownItem?.selectHandler()
        shouldCloseMenuOnOptionPress && setIsDropdownOpen((prev) => !prev)
      },
      setIsDropdownTouched: setIsDropdownTouched,
      menuValues: menuValues,
      customOptionsConfig: effectiveCustomOptionsConfig,
      dropdownMenuProps: dropdownMenuProps,
      selectedItemStyle: selectedItemStyle,
      getMenuItemStyle: getMenuItemStyle,
      listFooterComponent: listFooterComponent,
      listFooterComponentStyle: listFooterComponentStyle,
      simpleHeaderStyle: simpleHeaderStyle,
      useActiveTextColor: useActiveTextColor,
    }

    const MenuComponent = customDropdownMenuComponent ?? DropdownMenuComponent

    const labelNativeId = label ? `${testID}-label` : undefined
    const accessibilityLabelledBy = labelNativeId ? labelNativeId : undefined

    return (
      <View testID={tID(testID)} style={containerStyle}>
        <View nativeID={triggerButtonRefID}>
          {label && <BodyText text={label} size={BodyTextSize.CAPTION} nativeID={labelNativeId} />}
          <BaseButton
            onLayout={(event) => {
              buttonWidth.current = event.nativeEvent.layout.width
            }}
            buttonType={buttonType}
            rightIcon={
              hideChevron ? null : (
                <ChevronIcon direction={isDropdownOpen ? ChevronIconDirection.UP : ChevronIconDirection.DOWN} />
              )
            }
            testID={triggerTestID}
            style={style}
            size={buttonSize}
            fullWidth={fullWidth}
            leftIcon={leftIcon}
            leftIconNoMargin={leftIconNoMargin}
            iconAtEnd={iconAtEnd}
            iconColor={iconColor}
            loading={isLoading}
            disabled={isDisabled}
            textStyle={{ maxWidth: shouldTruncateButtonText ? '100%' : 'auto', ...customTextStyle }}
            text={
              buttonTextWidth ? (
                <ButtonTextContainer width={buttonTextContainerWidth}>
                  {shouldTruncateButtonText && !children ? (
                    <TruncatedText text={defaultButtonText} maxNumberOfLines={1} fontColor={customTextColor} noMargin />
                  ) : (
                    buttonText
                  )}
                </ButtonTextContainer>
              ) : (
                buttonText
              )
            }
            onPress={() => {
              onPress(!isDropdownOpen)
              setIsDropdownOpen((prev) => !prev)
            }}
            customTextSize={customTextSize}
            focusBorderStyle={focusBorderStyle}
            disableCenteredContent={disableCenteredContent}
            contentContainerStyle={contentContainerStyle}
            accessibilityLabel={accessibilityLabel}
            {...(accessibilityLabelledBy ? { accessibilityLabelledBy } : {})}
            {...(IS_WEB
              ? {
                  'aria-expanded': isDropdownOpen,
                  'aria-controls': dropdownContainerRefID,
                  'aria-haspopup': 'listbox',
                }
              : {})}
          />
          {buttonFooter}
        </View>
        {isDropdownOpen &&
          (showBottomSheet && bottomSheetProps ? (
            <BottomSheet
              ref={bottomSheetRef}
              onCloseEnd={() => {
                closeBottomSheet()
                setIsDropdownOpen(false)
              }}
              snapPoints={bottomSheetProps.snapPoints ?? [dropdownItemsBottomSheetMenuHeight]}
              enableContentPanningGesture={bottomSheetProps.enableContentPanningGesture}
              keyboardBehavior='interactive'
              keyboardBlurBehavior='restore'
              showCloseIcon={bottomSheetProps.showCloseIcon ?? true}
              useWebPortal={bottomSheetProps.useWebPortal}
              renderChildrenWithoutContainer={bottomSheetProps.renderChildrenWithoutContainer}
            >
              {!!customDropdownMenuComponent ? (
                <MenuComponent
                  {...dropdownMenuComponentSharedProps}
                  {...customDropdownMenuComponentProps}
                  scrollEnabledWithinBottomSheet={dropdownMenuProps?.scrollEnabledWithinBottomSheet}
                />
              ) : (
                <BottomSheetMenuContainer
                  height={dropdownItemsBottomSheetMenuHeight}
                  paddingTop={bottomSheetProps.topPadding}
                  paddingBottom={bottomSheetProps.bottomPadding}
                >
                  {bottomSheetProps.headerComponent}
                  <MenuComponent
                    {...dropdownMenuComponentSharedProps}
                    scrollEnabledWithinBottomSheet={dropdownMenuProps?.scrollEnabledWithinBottomSheet}
                  />
                  {bottomSheetProps.footerComponent}
                </BottomSheetMenuContainer>
              )}
            </BottomSheet>
          ) : (
            <DropdownContainer
              testID={tID(dropdownTestID ?? 'DropdownButton-dropdown')}
              width={dropdownWidth}
              maxWidth={menuMaxWidth}
              ref={dropdownContainerRef}
              nativeID={dropdownContainerRefID}
              style={dropdownStyle}
              accessibilityRole={IS_WEB ? ('listbox' as AccessibilityRole) : AccessibilityRolesNative.MENU}
              accessibilityLabel={
                dropdownAccessibilityLabel ??
                formatMessage({
                  defaultMessage: 'List of options',
                  description: 'Accessibility label for a list of options in a dropdown menu',
                })
              }
            >
              <MenuComponent {...dropdownMenuComponentSharedProps} {...customDropdownMenuComponentProps} />
            </DropdownContainer>
          ))}
      </View>
    )
  },
)
